"use client";

import { c as _c } from "react/compiler-runtime";
import { AccountLockup, AccountLockupFragment } from "@opensea/app-kit";
import { graphql, readFragment } from "@opensea/graphql";
import { EM_DASH } from "@opensea/i18n";
import { HandoffButton } from "@opensea/navigation";
import { Item, ItemContent, ItemTitle } from "@opensea/next-ui-kit";
import { Checkbox, classNames, FlexCenter, LiveAnimation, NumberDisplay, QuantityBadge, TABLE_SIZES, TableCell, TableRow, Truncate, tableRowSizeVariants, useTable } from "@opensea/ui-kit";
import { useSelectItemMouseShortcuts } from "@opensea/ui-kit/hooks";
import { useContextSelector } from "use-context-selector";
import { profileUrlFragment } from "@/components/account/utils";
import { LiveTimestamp } from "@/components/activity-feed/LiveTimestamp";
import { CollectionSettingsContext } from "@/components/collection/CollectionSettingsProvider";
import { useSideModule } from "@/components/layout/SideModuleProvider";
import { TokenPrice } from "@/components/price/TokenPrice";
import { TokenPriceFragment } from "@/components/price/TokenPrice.fragment";
import { ProfilePreviewTooltip } from "@/components/profile/ProfilePreviewTooltip/ProfilePreviewTooltip";
import { ProfilePreviewTooltipFragment } from "@/components/profile/ProfilePreviewTooltip/ProfilePreviewTooltip.fragment";
import { EnforcementBadge, EnforcementBadgeFragment } from "@/components/trustandsafety/EnforcementBadge";
import { useStatusQueryParam } from "@/hooks/query-param/use-status-query-param";
import { useAddress } from "@/hooks/wallet";
import { bestItemOfferFragment, getItemBestOffer } from "@/lib/fragments/best-order";
import { ItemAvatar, ItemAvatarFragment } from "../../avatar/ItemAvatar";
import { BulkActionsDisabledTooltip, BulkActionsDisabledTooltipFragment } from "../../BulkActionsDisabledTooltip";
import { BuyItemTableButton, BuyItemTableButtonFragment } from "../../BuyItemTableButton";
import { ItemLink, ItemLinkFragment } from "../../ItemLink";
import { ItemPreviewTooltip } from "../../ItemPreviewTooltip/ItemPreviewTooltip";
import { ItemPreviewTooltipFragment } from "../../ItemPreviewTooltip/ItemPreviewTooltip.fragment";
import { RarityTooltip, RarityTooltipFragment } from "../../rarity/RarityTooltip";
import { getItemOwnedQuantity, ItemOwnedQuantityFragment, itemUrlFragment } from "../../utils";
import { useCollectionItemsSelection } from "../use-collection-items-selection";
import { CollectionItemsTableSkeletonRow } from "./CollectionItemsTableSkeletonRow";
import { COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES } from "./styles";
export const CollectionItemsTableRowFragment = graphql(`
    fragment CollectionItemsTableRowFragment on Item {
      id
      name
      totalSupply
      isFungible
      ...ItemAvatar
      owner {
        ...profileUrl
        ...AccountLockup
        ...ProfilePreviewTooltip
      }
      bestListing {
        startTime
        pricePerItem {
          ...TokenPrice
        }
      }
      ...bestItemOffer
      rarity {
        rank
        category
      }
      lastSale {
        ...TokenPrice
      }
      enforcement {
        isCompromised
      }
      ...ItemLink
      ...BuyItemTableButton
      ...EnforcementBadge
      ...RarityTooltip
      ...ItemPreviewTooltip
      ...BulkActionsDisabledTooltip
      ...ItemOwnedQuantity
    }
  `, [itemUrlFragment, TokenPriceFragment, AccountLockupFragment, profileUrlFragment, BuyItemTableButtonFragment, ItemAvatarFragment, EnforcementBadgeFragment, ItemLinkFragment, RarityTooltipFragment, ItemPreviewTooltipFragment, BulkActionsDisabledTooltipFragment, bestItemOfferFragment, ProfilePreviewTooltipFragment, ItemOwnedQuantityFragment]);
export function CollectionItemsTableRow(t0) {
  const $ = _c(68);
  const {
    item: itemKey,
    index,
    context
  } = t0;
  const item = readFragment(CollectionItemsTableRowFragment, itemKey);
  const {
    statuses
  } = useStatusQueryParam();
  let t1;
  if ($[0] !== statuses) {
    t1 = statuses.includes("ownedByYou");
    $[0] = statuses;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showingOwnedByYou = t1;
  const ownedQuantity = getItemOwnedQuantity(item);
  const showRarity = useContextSelector(CollectionSettingsContext, _temp);
  const connectedAddress = useAddress();
  const {
    size
  } = useTable();
  const [allItems, selectedItems, selectItem, isItemSelectable] = useCollectionItemsSelection(_temp2);
  let t2;
  if ($[2] !== allItems || $[3] !== selectItem || $[4] !== selectedItems) {
    t2 = {
      items: allItems,
      selectedItems,
      selectItem
    };
    $[2] = allItems;
    $[3] = selectItem;
    $[4] = selectedItems;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    handleOnClick
  } = useSelectItemMouseShortcuts(t2);
  const {
    isSideModuleOpen
  } = useSideModule();
  if (!item) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CollectionItemsTableSkeletonRow />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const owner = item.owner;
  const {
    image,
    text
  } = tableRowSizeVariants({
    size
  });
  const startTime = item.bestListing?.startTime ? new Date(item.bestListing.startTime) : null;
  const isSelected = selectedItems.has(item.id);
  const disabled = !(isSelected || isItemSelectable(item.id));
  const bestOffer = getItemBestOffer(item);
  const t3 = item.id;
  let t4;
  if ($[7] !== disabled || $[8] !== handleOnClick || $[9] !== index) {
    t4 = e => {
      if (disabled) {
        return;
      }
      handleOnClick(e, index);
    };
    $[7] = disabled;
    $[8] = handleOnClick;
    $[9] = index;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== disabled || $[12] !== isSelected) {
    t5 = <Checkbox checked={isSelected} disabled={disabled} />;
    $[11] = disabled;
    $[12] = isSelected;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== disabled || $[15] !== item || $[16] !== t5) {
    t6 = <TableCell className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.checkbox}><BulkActionsDisabledTooltip disabled={disabled} item={item}>{t5}</BulkActionsDisabledTooltip></TableCell>;
    $[14] = disabled;
    $[15] = item;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const T0 = ItemContent;
  const T1 = FlexCenter;
  const t7 = "w-full gap-2";
  const T2 = Truncate;
  const T3 = ItemLink;
  const t8 = _temp3;
  const T4 = HandoffButton;
  const T5 = ItemTitle;
  const t9 = classNames(text(), "font-normal");
  let t10;
  if ($[18] !== T5 || $[19] !== item.name || $[20] !== t9) {
    t10 = <T5 className={t9} data-testid="ItemName">{item.name}</T5>;
    $[18] = T5;
    $[19] = item.name;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== T4 || $[23] !== t10) {
    t11 = <T4>{t10}</T4>;
    $[22] = T4;
    $[23] = t10;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== T3 || $[26] !== item || $[27] !== t11 || $[28] !== t8) {
    t12 = <T3 item={item} onClick={t8}>{t11}</T3>;
    $[25] = T3;
    $[26] = item;
    $[27] = t11;
    $[28] = t8;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== T2 || $[31] !== t12) {
    t13 = <T2>{t12}</T2>;
    $[30] = T2;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== item.isFungible || $[34] !== item.totalSupply || $[35] !== ownedQuantity || $[36] !== showingOwnedByYou) {
    t14 = item.isFungible ? <QuantityBadge>{showingOwnedByYou ? <NumberDisplay display="quantity" value={ownedQuantity} /> : <NumberDisplay display="quantity" value={item.totalSupply} />}</QuantityBadge> : null;
    $[33] = item.isFungible;
    $[34] = item.totalSupply;
    $[35] = ownedQuantity;
    $[36] = showingOwnedByYou;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== T1 || $[39] !== t13 || $[40] !== t14) {
    t15 = <T1 className={t7}>{t13}{t14}</T1>;
    $[38] = T1;
    $[39] = t13;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== T0 || $[43] !== t15) {
    t16 = <T0>{t15}</T0>;
    $[42] = T0;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== item || $[46] !== showRarity) {
    t17 = showRarity ? <TableCell align="right" className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.rarity}><RarityTooltip item={item}><NumberDisplay display="full" prefix="#" value={item.rarity?.rank} /></RarityTooltip></TableCell> : null;
    $[45] = item;
    $[46] = showRarity;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== item || $[49] !== size) {
    t18 = <TableCell align="right" className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.price}>{item.enforcement.isCompromised ? <NumberDisplay display="full" /> : <EnforcementBadge entity={item} renderWhenNotEnforced={<BuyItemTableButton item={item} size={size === "md" ? "sm" : "xs"} />} />}</TableCell>;
    $[48] = item;
    $[49] = size;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== bestOffer?.pricePerItem || $[52] !== item) {
    t19 = <TableCell align="right" className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.offer}>{item.enforcement.isCompromised ? <NumberDisplay display="full" /> : <EnforcementBadge entity={item} renderWhenNotEnforced={<TokenPrice price={bestOffer?.pricePerItem} />} />}</TableCell>;
    $[51] = bestOffer?.pricePerItem;
    $[52] = item;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] !== isSideModuleOpen) {
    t20 = classNames(COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.last, {
      hidden: isSideModuleOpen
    });
    $[54] = isSideModuleOpen;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== item.lastSale) {
    t21 = <TokenPrice price={item.lastSale} />;
    $[56] = item.lastSale;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t20 || $[59] !== t21) {
    t22 = <TableCell align="right" className={t20}>{t21}</TableCell>;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== connectedAddress || $[62] !== owner) {
    t23 = owner ? <ProfilePreviewTooltip profile={owner}><AccountLockup account={owner} addressDisplay="compact" connectedAddress={connectedAddress ?? null} disableTextOverflowTooltip={true} /></ProfilePreviewTooltip> : EM_DASH;
    $[61] = connectedAddress;
    $[62] = owner;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t23) {
    t24 = <TableCell align="right" className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.owner}>{t23}</TableCell>;
    $[64] = t23;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] !== startTime) {
    t25 = <TableCell align="right" className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.listed}><LiveTimestamp date={startTime} /></TableCell>;
    $[66] = startTime;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  return <LiveAnimation itemKey={t3} liveAddedAt={context?.itemsLiveAddedAt.get(item.id)} variant="table"><TableRow interactive={!disabled} onClick={t4}>{t6}<TableCell className={COLLECTION_ITEMS_TABLE_COLUMN_CLASSNAMES.item}><ItemPreviewTooltip item={item}><Item className="w-auto max-w-full" variant="unstyled"><ItemAvatar className={image()} item={item} size={TABLE_SIZES[size].image} />{t16}</Item></ItemPreviewTooltip></TableCell>{t17}{t18}{t19}{t22}{t24}{t25}</TableRow></LiveAnimation>;
}
function _temp3(e_0) {
  e_0.stopPropagation();
}
function _temp2(store) {
  return [store.allItems, store.selectedItems, store.selectItem, store.isItemSelectable];
}
function _temp(state) {
  return state.showRarity;
}